/** @format */

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { DrawerService } from "mis-design-system/drawer";
import { ToastService } from "mis-crystal-design-system/toast";
import { combineLatest, Subject, throwError } from "rxjs";
import { catchError, filter, map, take, takeUntil } from "rxjs/operators";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../constants/assets.constants";
import { NsEmpWebExpData } from "../../models/emp-web-exp.model";
import { MenuDirective } from "../../modules/menu/menu.directive";
import { ApiService } from "../../services/api.service";
import { BookingDataService } from "../../services/booking-data.service";
import { ConfigurationService } from "../../services/configuration.service";
import { HomeDataService } from "../../services/home-data.service";
import { LoaderService } from "../../services/loader.service";
import { WidgetService } from "../../services/widget.service";
import { BookingComponent } from "../modals/booking/booking.component";

const childRoutes = new Set(["calendar"]);

@Component({
  selector: "mis-employee-web-exp-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() showBackBtn = false;
  @ViewChild("menuOriginButton", { read: MenuDirective }) menuOrigin: MenuDirective;
  @ViewChild("dropdownButton", { read: MenuDirective }) statusDropdown: MenuDirective;
  configStream = this.cfgSvc.configurations;
  userInfo = this.homeDataSvc.employeeInfo;
  @Output() gotoHome = new EventEmitter(true);
  Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  endObs = new Subject<void>();
  customStatusChangeAllowed = false;
  currentStatus: NsEmpWebExpData.IEmployeeCustomStatus = {};
  defaultStatusList: NsEmpWebExpData.IEmployeeCustomStatus[] = [];
  displayStatusList: NsEmpWebExpData.IEmployeeCustomStatus[] = [];

  constructor(
    private cfgSvc: ConfigurationService,
    private homeDataSvc: HomeDataService,
    private bookingSvc: BookingDataService,
    private drawerSvc: DrawerService,
    private widgetSvc: WidgetService,
    private toast: ToastService,
    private apiSvc: ApiService,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    this.configStream.pipe(takeUntil(this.endObs)).subscribe(response => {
      this.customStatusChangeAllowed = response.flattenedSettings.employeeStatusModuleEnabled;
      if (this.customStatusChangeAllowed) {
        combineLatest([
          this.apiSvc.getLastStatus().pipe(
            catchError(err => {
              this.toast.displayMsg(this.toast.messageBuilder.error.simple);
              return throwError(`Failed to load last status. ${JSON.stringify(err)}`);
            })
          ),
          this.apiSvc.getCustomStatusList().pipe(
            catchError(err => {
              this.toast.displayMsg(this.toast.messageBuilder.error.simple);
              return throwError(`Failed to load status. ${JSON.stringify(err)}`);
            })
          ),
          this.widgetSvc.widgetAction.pipe(
            catchError(err => {
              this.toast.displayMsg(this.toast.messageBuilder.error.simple);
              return throwError(`Failed to load user action. ${JSON.stringify(err)}`);
            })
          )
        ])
          .pipe(takeUntil(this.endObs))
          .subscribe(([lastStatus, statusList, checkIn]) => {
            this.loader.show();
            this.defaultStatusList = [];
            this.defaultStatusList.push(...statusList.statusDisplayList);
            if (checkIn === "CHECK-IN") {
              this.setStatus("Online");
            } else if (lastStatus) {
              this.setStatus(lastStatus, true);
            } else {
              this.setStatus("Online");
            }
            this.loader.hide();
          });
      }
    });
  }

  setStatus(statusName: string, fetchLastStatus?: boolean): void {
    let statusObject;
    if (fetchLastStatus) {
      statusObject = this.defaultStatusList.find(item => item.statusName === decodeURIComponent(statusName));
    } else statusObject = this.defaultStatusList.find(item => item.statusName === statusName);
    this.currentStatus = statusObject;
    this.handleStatusChange(statusObject);
  }

  quickActionEvent(act: string): void {
    this.menuOrigin?.close();
    if (act === "OFFICE" || act === "WFH") {
      combineLatest([this.cfgSvc.configurations, this.homeDataSvc.employeeInfo])
        .pipe(take(1))
        .subscribe(([configurations, empData]) => {
          const configs = this.bookingSvc.setFormForCreate(configurations, empData, act);
          const ref = this.drawerSvc.show(BookingComponent, configs);
          ref.afterClosed.subscribe(res => {
            if (res === "DONE") {
              this.widgetSvc.refreshWidgets("BOOKINGS");
            }
          });
        });
    }
  }
  handleStatusChange(status: NsEmpWebExpData.IEmployeeCustomStatus): void {
    this.loader.show();
    this.apiSvc
      .captureStatusChange(status)
      .pipe(
        catchError(err => {
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to change status. ${JSON.stringify(err)}`);
        })
      )
      .subscribe(response => {
        if (response) {
          this.currentStatus = status;
          this.filterOutSelectedStatus();
        }
        this.loader.hide();
      });
    this.statusDropdown?.close();
  }

  back(): void {
    this.gotoHome.emit();
  }

  filterOutSelectedStatus(): void {
    this.displayStatusList = this.defaultStatusList.filter(item => item.statusName !== this.currentStatus.statusName);
  }

  ngOnDestroy(): void {
    this.endObs.next();
    this.endObs.complete();
  }
}
